import { action, computed, makeObservable, observable, reaction, IReactionDisposer } from "mobx";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { buildOnboardingContext } from "./onboarding-context";
import { ONBOARDING_STEP_DEFINITIONS } from "./onboarding-step-definitions";
import type { MDSIconKind } from "@/mds/components/icon/icon/icon.types";
import type {
  OnboardingContext,
  OnboardingStepDefinition,
  OnboardingStepId,
  OnboardingSubstepId,
} from "./onboarding-types";

export type OnboardingStepperStatus = "completed" | "active" | "upcoming";

export interface OnboardingStepperStep {
  id: OnboardingStepId;
  icon: MDSIconKind;
  status: OnboardingStepperStatus;
}

const NON_SKIPPABLE_TRIAL_COMPLETION_RETRY_INITIAL_MS = 1_000;
const NON_SKIPPABLE_TRIAL_COMPLETION_RETRY_MAX_MS = 30_000;
const EXISTING_USER_MEM_AGENT_ONBOARDING_STEP_IDS: ReadonlySet<OnboardingStepId> = new Set([
  "existing-user-mem-agent-intro",
  "existing-user-proactive-trial",
  "existing-user-mem-agent-first-message",
]);
const EXISTING_USER_MEM_AGENT_INTRO_STEP_ID = "existing-user-mem-agent-intro";
const EXISTING_USER_PROACTIVE_TRIAL_STEP_ID = "existing-user-proactive-trial";
const EXISTING_USER_MEM_AGENT_FIRST_MESSAGE_STEP_ID = "existing-user-mem-agent-first-message";
const NEW_USER_MEM_AGENT_ONBOARDING_STEP_IDS: ReadonlySet<OnboardingStepId> = new Set([
  "mem-agent-shortcut",
  "mem-agent-first-message",
]);
const EXISTING_USER_MEM_AGENT_NON_BLOCKING_PREVIOUS_STEP_IDS: ReadonlySet<OnboardingStepId> =
  new Set(["native-app-download", ...EXISTING_USER_MEM_AGENT_ONBOARDING_STEP_IDS]);

/**
 * Centralized onboarding store.
 *
 * Derives all onboarding state from a single ordered step pipeline.
 * Each step declares when it is applicable and when it is completed,
 * so the store can compute currentStep, progress, and overlay visibility
 * without imperative navigation logic.
 */
export class OnboardingStore extends AppSubStore {
  private reactionDisposers: IReactionDisposer[] = [];
  private nonSkippableTrialCompletionRetriesAreActive = false;
  private nonSkippableTrialCompletionRetryAttempt = 0;
  private nonSkippableTrialCompletionRetryTimer?: ReturnType<typeof setTimeout>;
  proactiveCheckoutReturnConfirmationIsPending = false;
  existingUserProactiveTrialWelcomeIsPending = false;
  hasInitialized = false;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<
      OnboardingStore,
      | "reactionDisposers"
      | "nonSkippableTrialCompletionRetriesAreActive"
      | "nonSkippableTrialCompletionRetryAttempt"
      | "nonSkippableTrialCompletionRetryTimer"
      | "persistNonSkippableTrialOnboardingCompletion"
      | "scheduleNonSkippableTrialOnboardingCompletionRetry"
      | "clearNonSkippableTrialOnboardingCompletionRetry"
      | "evaluateExistingUserMemAgentOnboarding"
      | "migrateCompletedExistingUserMemAgentOnboardingToSyncedState"
      | "hasCompletedOnboardingBeforeExistingUserMemAgentSteps"
    >(this, {
      reactionDisposers: false,
      nonSkippableTrialCompletionRetriesAreActive: false,
      nonSkippableTrialCompletionRetryAttempt: false,
      nonSkippableTrialCompletionRetryTimer: false,
      proactiveCheckoutReturnConfirmationIsPending: observable,
      existingUserProactiveTrialWelcomeIsPending: observable,
      hasInitialized: observable,
      persistNonSkippableTrialOnboardingCompletion: false,
      scheduleNonSkippableTrialOnboardingCompletionRetry: false,
      clearNonSkippableTrialOnboardingCompletionRetry: false,
      evaluateExistingUserMemAgentOnboarding: false,
      migrateCompletedExistingUserMemAgentOnboardingToSyncedState: false,
      hasCompletedOnboardingBeforeExistingUserMemAgentSteps: false,
      dispose: false,
      context: computed,
      allSteps: computed,
      applicableSteps: computed,
      pendingSteps: computed,
      completedSteps: computed,
      currentStep: computed,
      currentStepId: computed,
      currentSubstepId: computed,
      hasNextStep: computed,
      showFinish: computed,
      isActive: computed,
      progress: computed,
      stepperSteps: computed,
      completeCurrentStep: action,
      startProactiveCheckoutReturnConfirmation: action,
      finishProactiveCheckoutReturnConfirmation: action,
      startExistingUserProactiveTrialWelcome: action,
      finishExistingUserProactiveTrialWelcome: action,
      debugResetAll: action,
      initialize: action,
    });
  }

  /** Sets up tracking reactions for onboarding state transitions. */
  async initialize() {
    this.dispose();
    this.hasInitialized = false;
    this.nonSkippableTrialCompletionRetriesAreActive = true;

    const shouldLoadProactiveTrialOfferAssignments = () => {
      const context = this.context;

      return (
        context.onboardingFlowVersion !== "intent_v1" &&
        context.isEligibleForProactiveTrialOnboarding &&
        !context.hasProactiveAccess &&
        !context.hasCompletedOrSkippedProactiveTrialOnboarding &&
        context.proactiveTrialOnboardingServerProgress !== "completed" &&
        (context.proactiveTrialOnboardingDebugOverride ||
          (context.isProactiveTrialEligibilityAuthoritative && context.isProactiveTrialEligible))
      );
    };
    const shouldLoadNonSkippableTrialAssignment = () => {
      const context = this.context;
      const shouldReconcileProactiveAccess =
        context.hasProactiveAccess &&
        (context.proactiveTrialOnboardingIsRelevant ||
          context.hasCompletedOrSkippedProactiveTrialOnboarding);
      const shouldReconcileCompletedOnboarding =
        context.hasCompletedOrSkippedProactiveTrialOnboarding || shouldReconcileProactiveAccess;

      return (
        shouldLoadProactiveTrialOfferAssignments() ||
        (context.isEligibleForProactiveTrialOnboarding &&
          context.isProactiveTrialEligibilityAuthoritative &&
          shouldReconcileCompletedOnboarding)
      );
    };
    const initialNonSkippableTrialAssignmentPromise = shouldLoadNonSkippableTrialAssignment()
      ? this.store.subscriptions.fetchNonSkippableTrialAssignment()
      : undefined;
    const initialProactivePricingAssignmentPromise = shouldLoadProactiveTrialOfferAssignments()
      ? this.store.subscriptions.preloadProactivePricingAssignment()
      : undefined;

    this.reactionDisposers.push(
      reaction(
        () => ({
          hasProactiveAccess: this.context.hasProactiveAccess,
          isEligibleForProactiveTrialOnboarding: this.context.isEligibleForProactiveTrialOnboarding,
          isProactiveTrialEligibilityAuthoritative:
            this.context.isProactiveTrialEligibilityAuthoritative,
          isProactiveTrialEligible: this.context.isProactiveTrialEligible,
          proactiveTrialOnboardingDebugOverride: this.context.proactiveTrialOnboardingDebugOverride,
          shouldShowIcpRfi: this.context.shouldShowIcpRfi,
        }),
        ({
          hasProactiveAccess,
          isEligibleForProactiveTrialOnboarding,
          isProactiveTrialEligibilityAuthoritative,
          isProactiveTrialEligible,
          proactiveTrialOnboardingDebugOverride,
          shouldShowIcpRfi,
        }) => {
          const shouldActivateProactiveTrialOnboarding =
            this.context.onboardingFlowVersion !== "intent_v1" &&
            isEligibleForProactiveTrialOnboarding &&
            shouldShowIcpRfi &&
            !hasProactiveAccess &&
            (proactiveTrialOnboardingDebugOverride ||
              (isProactiveTrialEligibilityAuthoritative && isProactiveTrialEligible));

          if (shouldActivateProactiveTrialOnboarding) {
            this.store.platformInfo.perDeviceOnboardingInfo.activateProactiveTrialOnboarding();
          }
        },
        { fireImmediately: true }
      ),
      reaction(
        () => ({
          hasCompletedOrSkippedProactiveTrialOnboarding:
            this.context.hasCompletedOrSkippedProactiveTrialOnboarding,
          hasProactiveAccess: this.context.hasProactiveAccess,
          isFloatingAgentOnboardingSurfaceAvailable:
            this.context.isFloatingAgentOnboardingSurfaceAvailable,
          isEligibleForProactiveTrialOnboarding: this.context.isEligibleForProactiveTrialOnboarding,
          nonSkippableTrialAssignmentCanSkip: this.context.nonSkippableTrialAssignmentCanSkip,
          proactiveTrialOnboardingIsRelevant: this.context.proactiveTrialOnboardingIsRelevant,
          proactiveTrialOnboardingServerProgress:
            this.context.proactiveTrialOnboardingServerProgress,
        }),
        ({
          hasCompletedOrSkippedProactiveTrialOnboarding,
          hasProactiveAccess,
          isFloatingAgentOnboardingSurfaceAvailable,
          isEligibleForProactiveTrialOnboarding,
          nonSkippableTrialAssignmentCanSkip,
          proactiveTrialOnboardingIsRelevant,
          proactiveTrialOnboardingServerProgress,
        }) => {
          if (
            hasProactiveAccess &&
            isEligibleForProactiveTrialOnboarding &&
            (proactiveTrialOnboardingIsRelevant ||
              proactiveTrialOnboardingServerProgress === "pending") &&
            !hasCompletedOrSkippedProactiveTrialOnboarding
          ) {
            if (isFloatingAgentOnboardingSurfaceAvailable) {
              this.store.platformInfo.perDeviceOnboardingInfo.activateMemAgentOnboarding();
            }
            this.store.platformInfo.perDeviceOnboardingInfo.completeOrSkipProactiveTrialOnboarding();
          }

          const shouldPersistOnboardingCompletion =
            this.context.onboardingFlowVersion !== "intent_v1" &&
            isEligibleForProactiveTrialOnboarding &&
            proactiveTrialOnboardingServerProgress === "pending" &&
            (hasCompletedOrSkippedProactiveTrialOnboarding || hasProactiveAccess) &&
            (nonSkippableTrialAssignmentCanSkip === true || hasProactiveAccess);

          if (shouldPersistOnboardingCompletion) {
            void this.persistNonSkippableTrialOnboardingCompletion();
          } else {
            this.clearNonSkippableTrialOnboardingCompletionRetry();
          }
        },
        { fireImmediately: true }
      ),
      reaction(shouldLoadNonSkippableTrialAssignment, shouldLoadNonSkippableTrialAssignment => {
        if (shouldLoadNonSkippableTrialAssignment) {
          void this.store.subscriptions.fetchNonSkippableTrialAssignment();
        }
      }),
      reaction(
        shouldLoadProactiveTrialOfferAssignments,
        shouldLoadProactiveTrialOfferAssignments => {
          if (shouldLoadProactiveTrialOfferAssignments) {
            void this.store.subscriptions.preloadProactivePricingAssignment();
          }
        }
      ),
      reaction(
        () => ({
          existingUserMemAgentOnboardingDeferredUntilAccess:
            this.context.existingUserMemAgentOnboardingDeferredUntilAccess,
          existingUserMemAgentOnboardingIsRelevant:
            this.context.existingUserMemAgentOnboardingIsRelevant,
          existingUserMemAgentOnboardingNeedsEvaluation:
            this.context.existingUserMemAgentOnboardingNeedsEvaluation,
          hasCompletedExistingUserMemAgentFirstMessageOnboardingOnThisDevice:
            this.store.platformInfo.perDeviceOnboardingInfo
              .hasCompletedExistingUserMemAgentFirstMessageOnboarding,
          hasCompletedExistingUserMemAgentIntroOnboardingOnThisDevice:
            this.store.platformInfo.perDeviceOnboardingInfo
              .hasCompletedExistingUserMemAgentIntroOnboarding,
          hasCompletedOnboardingBeforeExistingUserMemAgentSteps:
            this.hasCompletedOnboardingBeforeExistingUserMemAgentSteps(),
          hasCompletedOrSkippedProactiveTrialOnboarding:
            this.context.hasCompletedOrSkippedProactiveTrialOnboarding,
          isMemAgentAccessAuthoritative: this.context.isMemAgentAccessAuthoritative,
          isMemAgentAccessEnabled: this.context.isMemAgentAccessEnabled,
          memAgentOnboardingIsRelevant: this.context.memAgentOnboardingIsRelevant,
        }),
        () => this.evaluateExistingUserMemAgentOnboarding()
      ),
      reaction(
        () => this.currentStepId,
        (toStepId: OnboardingStepId | undefined, fromStepId: OnboardingStepId | undefined) => {
          if (toStepId === undefined && fromStepId !== undefined) {
            trackEvent(TrackedEvent.OnboardingComplete, {
              total_steps: this.applicableSteps.length,
              onboarding_flow_version: this.context.onboardingFlowVersion ?? undefined,
              onboarding_intent: this.context.onboardingIntent ?? undefined,
            });
          } else {
            trackEvent(TrackedEvent.OnboardingStepChange, {
              from_step_id: fromStepId,
              to_step_id: toStepId,
              completed_count: this.completedSteps.length,
              total_count: this.applicableSteps.length,
              onboarding_flow_version: this.context.onboardingFlowVersion ?? undefined,
              onboarding_intent: this.context.onboardingIntent ?? undefined,
            });
          }
        }
      ),
      reaction(
        () => this.currentSubstepId,
        (
          toSubstepId: OnboardingSubstepId | undefined,
          fromSubstepId: OnboardingSubstepId | undefined
        ) => {
          trackEvent(TrackedEvent.OnboardingSubstepChange, {
            step_id: this.currentStepId,
            from_substep_id: fromSubstepId,
            to_substep_id: toSubstepId,
            onboarding_flow_version: this.context.onboardingFlowVersion ?? undefined,
            onboarding_intent: this.context.onboardingIntent ?? undefined,
          });
        }
      )
    );

    // Resolve both offer decisions before the app can render the trial step. The
    // non-skippable assignment also owns account-shared completion.
    await Promise.all([
      initialNonSkippableTrialAssignmentPromise,
      initialProactivePricingAssignmentPromise,
    ]);

    this.evaluateExistingUserMemAgentOnboarding();
    if (
      this.context.onboardingFlowVersion === "intent_v1" &&
      this.context.isDesktop &&
      !this.context.intentOnboardingIsCompleted
    ) {
      trackEvent(TrackedEvent.IntentOnboardingNativeResume, {
        onboarding_intent: this.context.onboardingIntent,
        resumed_step_id: this.currentStepId,
      });
    }
    this.hasInitialized = true;
  }

  /**
   * Converts legacy per-device onboarding records into the existing-user Mem Agent flow.
   *
   * Devices without floating access are deferred instead of dismissed, so an
   * account promoted from chat-only can show the same real onboarding pages
   * after access is granted.
   */
  private evaluateExistingUserMemAgentOnboarding() {
    const context = this.context;
    if (context.onboardingFlowVersion === "intent_v1") {
      return;
    }
    this.migrateCompletedExistingUserMemAgentOnboardingToSyncedState();

    if (context.existingUserMemAgentOnboardingIsRelevant) {
      return;
    }

    const hasExistingUserMemAgentAccess = context.isMemAgentAccessEnabled;

    const shouldEvaluateExistingUserFlow =
      context.existingUserMemAgentOnboardingNeedsEvaluation ||
      context.existingUserMemAgentOnboardingDeferredUntilAccess;

    if (!shouldEvaluateExistingUserFlow) {
      return;
    }

    const hasCompletedPreviousOnboarding =
      this.hasCompletedOnboardingBeforeExistingUserMemAgentSteps();

    if (!hasExistingUserMemAgentAccess) {
      if (
        context.existingUserMemAgentOnboardingNeedsEvaluation &&
        !context.memAgentOnboardingIsRelevant &&
        hasCompletedPreviousOnboarding
      ) {
        this.store.platformInfo.perDeviceOnboardingInfo.deferExistingUserMemAgentOnboardingUntilAccess();
      }
      return;
    }

    // Pre-launch proactive users can carry a generic Mem Agent onboarding flag
    // from checkout before floating access exists; the catch-up flow owns them.
    const canReplaceStaleNewUserMemAgentFlow =
      context.memAgentOnboardingIsRelevant && !context.isEligibleForProactiveTrialOnboarding;
    const shouldActivateExistingUserFlow =
      (!context.memAgentOnboardingIsRelevant || canReplaceStaleNewUserMemAgentFlow) &&
      hasCompletedPreviousOnboarding;

    if (shouldActivateExistingUserFlow) {
      this.store.platformInfo.perDeviceOnboardingInfo.activateExistingUserMemAgentOnboarding();
      return;
    }

    this.store.platformInfo.perDeviceOnboardingInfo.dismissExistingUserMemAgentOnboarding();
  }

  /**
   * Promotes the old device-local page completions into account-shared task IDs
   * before the synced predicates can replay those pages on this device.
   */
  private migrateCompletedExistingUserMemAgentOnboardingToSyncedState() {
    const onboardingInfo = this.store.platformInfo.onboardingInfo;
    const perDeviceOnboardingInfo = this.store.platformInfo.perDeviceOnboardingInfo;
    const completedStepIds: OnboardingStepId[] = [];

    if (perDeviceOnboardingInfo.hasCompletedExistingUserMemAgentIntroOnboarding) {
      completedStepIds.push(EXISTING_USER_MEM_AGENT_INTRO_STEP_ID);
    }

    if (perDeviceOnboardingInfo.hasCompletedOrSkippedProactiveTrialOnboarding) {
      completedStepIds.push(EXISTING_USER_PROACTIVE_TRIAL_STEP_ID);
    }

    if (perDeviceOnboardingInfo.hasCompletedExistingUserMemAgentFirstMessageOnboarding) {
      completedStepIds.push(EXISTING_USER_MEM_AGENT_FIRST_MESSAGE_STEP_ID);
    }

    const unsyncedStepIds = completedStepIds.filter(
      stepId => !onboardingInfo.hasCompletedTask(stepId)
    );

    if (unsyncedStepIds.length > 0) {
      void onboardingInfo.completeOnboardingTasks(unsyncedStepIds);
    }
  }

  /** Checks whether the onboarding steps before the existing-user Agent intro are complete. */
  private hasCompletedOnboardingBeforeExistingUserMemAgentSteps(): boolean {
    const ctx = this.context;
    const existingUserMemAgentIntroStepIndex = ONBOARDING_STEP_DEFINITIONS.findIndex(
      step => step.id === EXISTING_USER_MEM_AGENT_INTRO_STEP_ID
    );

    return ONBOARDING_STEP_DEFINITIONS.every((step, stepIndex) => {
      if (
        existingUserMemAgentIntroStepIndex >= 0 &&
        stepIndex >= existingUserMemAgentIntroStepIndex
      ) {
        return true;
      }

      if (EXISTING_USER_MEM_AGENT_NON_BLOCKING_PREVIOUS_STEP_IDS.has(step.id)) {
        return true;
      }

      if (
        !ctx.isEligibleForProactiveTrialOnboarding &&
        NEW_USER_MEM_AGENT_ONBOARDING_STEP_IDS.has(step.id)
      ) {
        return true;
      }

      if (!step.applicableWhen(ctx)) {
        return true;
      }

      return step.completedWhen(ctx);
    });
  }

  /**
   * Reconciles local completion to the shared assignment with capped backoff.
   * Retries stop when the assignment resolves, becomes unsafe to complete, or
   * this store is disposed.
   */
  private persistNonSkippableTrialOnboardingCompletion = async () => {
    const completed = await this.store.subscriptions.completeNonSkippableTrialOnboarding();

    if (completed) {
      this.clearNonSkippableTrialOnboardingCompletionRetry();
      return;
    }

    const context = this.context;
    if (
      this.nonSkippableTrialCompletionRetriesAreActive &&
      context.isEligibleForProactiveTrialOnboarding &&
      context.proactiveTrialOnboardingServerProgress === "pending" &&
      (context.hasCompletedOrSkippedProactiveTrialOnboarding || context.hasProactiveAccess) &&
      (context.nonSkippableTrialAssignmentCanSkip === true || context.hasProactiveAccess)
    ) {
      this.scheduleNonSkippableTrialOnboardingCompletionRetry();
    }
  };

  private scheduleNonSkippableTrialOnboardingCompletionRetry() {
    if (this.nonSkippableTrialCompletionRetryTimer) {
      return;
    }

    const delay = Math.min(
      NON_SKIPPABLE_TRIAL_COMPLETION_RETRY_INITIAL_MS *
        2 ** this.nonSkippableTrialCompletionRetryAttempt,
      NON_SKIPPABLE_TRIAL_COMPLETION_RETRY_MAX_MS
    );
    this.nonSkippableTrialCompletionRetryAttempt += 1;
    this.nonSkippableTrialCompletionRetryTimer = setTimeout(() => {
      this.nonSkippableTrialCompletionRetryTimer = undefined;
      void this.persistNonSkippableTrialOnboardingCompletion();
    }, delay);
  }

  private clearNonSkippableTrialOnboardingCompletionRetry() {
    if (this.nonSkippableTrialCompletionRetryTimer) {
      clearTimeout(this.nonSkippableTrialCompletionRetryTimer);
      this.nonSkippableTrialCompletionRetryTimer = undefined;
    }
    this.nonSkippableTrialCompletionRetryAttempt = 0;
  }

  /** Tears down tracking reactions and pending retries. */
  dispose() {
    this.nonSkippableTrialCompletionRetriesAreActive = false;
    this.reactionDisposers.forEach(d => d());
    this.reactionDisposers = [];
    this.clearNonSkippableTrialOnboardingCompletionRetry();
  }

  /** Snapshot of all state the step predicates need. */
  get context(): OnboardingContext {
    return buildOnboardingContext(this.store);
  }

  /** All step definitions (including non-applicable). Used by debug overlay. */
  get allSteps(): OnboardingStepDefinition[] {
    return ONBOARDING_STEP_DEFINITIONS;
  }

  /** Steps that are applicable given the current context. */
  get applicableSteps(): OnboardingStepDefinition[] {
    return ONBOARDING_STEP_DEFINITIONS.filter(step => step.applicableWhen(this.context));
  }

  /** Applicable steps that are not yet completed. */
  get pendingSteps(): OnboardingStepDefinition[] {
    const ctx = this.context;
    return this.applicableSteps.filter(step => !step.completedWhen(ctx));
  }

  /** Applicable steps that have been completed. */
  get completedSteps(): OnboardingStepDefinition[] {
    const ctx = this.context;
    return this.applicableSteps.filter(step => step.completedWhen(ctx));
  }

  /** The first applicable step that is not yet completed, or undefined if all done. */
  get currentStep(): OnboardingStepDefinition | undefined {
    if (this.proactiveCheckoutReturnConfirmationIsPending) {
      const proactiveTrialStep = this.applicableSteps.find(
        step =>
          step.id === "proactive-trial" ||
          step.id === "intent-proactive-trial" ||
          step.id === "existing-user-proactive-trial"
      );

      if (proactiveTrialStep) {
        return proactiveTrialStep;
      }
    }

    return this.pendingSteps[0];
  }

  /** The ID of the current step, or undefined if all done. */
  get currentStepId(): OnboardingStepId | undefined {
    return this.currentStep?.id;
  }

  /** The wizard substep ID to display. Resolves via substep activeWhen predicates. */
  get currentSubstepId(): OnboardingSubstepId | undefined {
    const step = this.currentStep;
    if (!step) return undefined;

    const match = step.substeps.find(s => s.activeWhen(this.context));
    return match?.id ?? step.substeps[0].id;
  }

  /** Whether there is another step after the current one. */
  get hasNextStep(): boolean {
    return this.pendingSteps.length > 1;
  }

  /**
   * Whether the current substep is the final exit point of the entire
   * onboarding pipeline. Components use this to show "Finish" vs "Next".
   */
  get showFinish(): boolean {
    const step = this.currentStep;
    if (!step) return false;

    if (this.hasNextStep) return false;

    // The active substep is the last one in the definition array
    const activeSubstep = step.substeps.find(s => s.activeWhen(this.context));
    const lastSubstep = step.substeps[step.substeps.length - 1];
    return activeSubstep?.id === lastSubstep.id;
  }

  /** Whether onboarding is active (has an incomplete step). */
  get isActive(): boolean {
    return this.currentStep !== undefined;
  }

  /** Progress as a fraction [0, 1]. */
  get progress(): number {
    const total = this.applicableSteps.length;
    if (total === 0) return 1;
    return this.completedSteps.length / total;
  }

  /** Steps that participate in the stepper UI, with their completion status. */
  get stepperSteps(): OnboardingStepperStep[] {
    const ctx = this.context;
    const currentId = this.currentStepId;
    let foundCurrent = false;

    return this.applicableSteps
      .filter(
        (step): step is OnboardingStepDefinition & { stepperIcon: MDSIconKind } =>
          step.stepperIcon !== undefined
      )
      .map(step => {
        if (step.completedWhen(ctx)) {
          return {
            id: step.id,
            icon: step.stepperIcon,
            status: "completed" as const,
          };
        }
        if (step.id === currentId && !foundCurrent) {
          foundCurrent = true;
          return {
            id: step.id,
            icon: step.stepperIcon,
            status: "active" as const,
          };
        }
        return {
          id: step.id,
          icon: step.stepperIcon,
          status: "upcoming" as const,
        };
      });
  }

  /**
   * Complete the current step by calling its declarative complete action.
   *
   * Each step definition carries its own complete function, so this method
   * simply delegates — no switch statement needed.
   */
  async completeCurrentStep(completedSteps: string[] = []) {
    const step = this.currentStep;
    if (!step) return;
    await step.complete(this.store, completedSteps, this.context);
  }

  /** Marks a Proactive checkout return as pending while the trial page stays visible. */
  startProactiveCheckoutReturnConfirmation() {
    this.proactiveCheckoutReturnConfirmationIsPending = true;
  }

  /** Clears the checkout-return pending state after Stripe confirmation settles. */
  finishProactiveCheckoutReturnConfirmation() {
    this.proactiveCheckoutReturnConfirmationIsPending = false;
  }

  /** Marks the existing-user checkout success modal as owning the next onboarding transition. */
  startExistingUserProactiveTrialWelcome() {
    this.existingUserProactiveTrialWelcomeIsPending = true;
  }

  /** Clears existing-user checkout success ownership after the trial step is advanced. */
  finishExistingUserProactiveTrialWelcome() {
    this.existingUserProactiveTrialWelcomeIsPending = false;
  }

  /** Reset every step with a debug handler, including steps that are currently inapplicable. */
  async debugResetAll() {
    await Promise.all(
      this.allSteps.filter(step => step.debugReset).map(step => step.debugReset!(this.store))
    );
  }
}
